import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDMY } from "@/lib/dateFormat";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

const ORIGEN = { teacher: "La profesora", admin: "La administración", student: "El estudiante" };

/** Justificadas dadas con menos aviso del que pide la política: las que conviene
 *  revisar y, si corresponde, cobrar la clase. */
export default function OutOfPolicyJustified() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/absence-requests/out-of-policy");
        setRows(r.data?.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="out-of-policy-report">
      <div>
        <h3 className="font-semibold text-slate-900">Justificadas fuera de política</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Clases justificadas con menos aviso del que pide la política del estudiante.
          Desde el 01/10/2026 la profesora ya no puede darlas: quedan pendientes de su revisión.
        </p>
      </div>

      {!rows.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500"
             data-testid="out-of-policy-empty">
          <CheckCircle2 className="h-5 w-5 mx-auto mb-2 text-emerald-500" />
          No hay justificadas fuera de política en este período.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clase</TableHead>
                <TableHead>Estudiante</TableHead>
                <TableHead>Grupo</TableHead>
                <TableHead>Quién la justificó</TableHead>
                <TableHead className="text-right">Aviso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.request_id}-${r.date}`} data-testid={`oop-row-${r.request_id}-${r.date}`}>
                  <TableCell className="whitespace-nowrap text-sm">{formatDMY(r.date)}</TableCell>
                  <TableCell className="font-medium">{r.student_name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.group_name}</TableCell>
                  <TableCell className="text-sm">
                    <div>{ORIGEN[r.origin] || r.origin}</div>
                    {r.marked_by && <div className="text-[11px] text-slate-500">{r.marked_by}</div>}
                    {r.submitted_at_cr && (
                      <div className="text-[11px] text-slate-400">
                        {formatDMY(r.submitted_at_cr.slice(0, 10))} · {r.submitted_at_cr.slice(11, 16)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline"
                           className="bg-amber-50 text-amber-800 border-amber-200 border gap-1 inline-flex">
                      <AlertTriangle className="h-3 w-3" />
                      {r.hours_notice != null ? `${Math.round(r.hours_notice * 10) / 10} h` : "—"}
                      {r.policy_hours ? ` / ${r.policy_hours} h` : ""}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
